import { mergeMap, catchError } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { EMPTY, of } from 'rxjs';
import get from 'lodash.get';

import { NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_INFO } from 'ui-components/services/notification-service';
import { NotificationHubService } from '../../notification-hub/notification-hub-service';
import { NotificationType } from '../../notification-hub/notification-hub.types';
import { notificationDescriptionMessages } from '../../notification-hub/notification-messages';
import { ModalService } from 'ui-components/services/modal-service';
import { ExportShipmentsContainer } from '../../../components/modals/export-records-modal/containers/export-shipments-container';
import { ExportBatchShipmentsContainer } from '../../../components/modals/export-records-modal/containers/export-batch-shipments-container';
import Global from '../../../store/global-selectors';
import { DocumentService } from '../../../services/document-service';
import * as ExportApi from './export-shipments.api';
import * as Actions from './export-shipments.actions';
import { getIsOrdersPage } from '../../../store/orders/orders.helpers';
import { ShipmentExportHelper } from './export-helper';
import { messages } from '../messages';

const NOTIFICATION_DURATION_IN_MS = 5000;

const showSuccessNotification = () => {
  NotificationHubService.showNotification(
    {
      notificationType: NotificationType.SHIPMENTS_EXPORT_STARTED,
      defaultTitle: messages.shipmentExportNotifHeader.defaultMessage,
      defaultDescription: messages.shipmentExportNotifBody.defaultMessage,
    },
    {
      notificationType: NOTIFICATION_TYPE_INFO,
      title: <FormattedMessage {...messages.shipmentExportNotifHeader} />,
      content: <FormattedMessage {...messages.shipmentExportNotifBody} />,
      duration: NOTIFICATION_DURATION_IN_MS,
    },
  );
};

const showFailureNotification = () => {
  NotificationHubService.showNotification(
    {
      notificationType: NotificationType.SHIPMENTS_EXPORT_FAILED,
      defaultTitle: messages.shipmentExportErrorHeader.defaultMessage,
      defaultDescription: messages.shipmentExportErrorBody.defaultMessage,
    },
    {
      notificationType: NOTIFICATION_TYPE_ERROR,
      title: <FormattedMessage {...messages.shipmentExportErrorHeader} />,
      content: <FormattedMessage {...messages.shipmentExportErrorBody} />,
      duration: NOTIFICATION_DURATION_IN_MS,
    },
  );
};

export const openExportShipmentsModalEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.OPEN_EXPORT_SHIPMENTS_MODAL),
    mergeMap(({ buildApiFilters }) => {
      ModalService.launchModal({
        component: ExportShipmentsContainer,
        props: {
          buildApiFilters,
        },
      });
      return EMPTY;
    }),
  );
};

export const openExportBatchShipmentsModalEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.OPEN_EXPORT_BATCH_SHIPMENTS_MODAL),
    mergeMap(() => {
      ModalService.launchModal({
        component: ExportBatchShipmentsContainer,
        props: {
          isOrdersGridShipmentMode: getIsOrdersPage(),
        },
      });
      return EMPTY;
    }),
  );
};

export const initiateShipmentExportEpic = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(Actions.EXPORT_SHIPMENTS_REQUEST),
    mergeMap((action) => {
      const { viewRecordsOrSelectedRecords, primaryRecordsOrLineItems, intl, filters, isOnAdvancedSearch } = action;
      const state = getState();
      const { initiatedByUserId, requestUrl, requestBody } = ShipmentExportHelper.getExportRequestParameters(
        state,
        viewRecordsOrSelectedRecords,
        primaryRecordsOrLineItems,
        intl,
        isOnAdvancedSearch,
        filters,
        history,
      );

      return ExportApi.exportShipments(requestUrl, requestBody).pipe(
        mergeMap((jobId) => {
          showSuccessNotification();
          return of(Actions.exportShipmentsSuccess(jobId, initiatedByUserId));
        }),
        catchError(() => {
          showFailureNotification();
          return EMPTY;
        }),
      );
    }),
  );
};

export const initiateBatchShipmentExportEpic = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(Actions.EXPORT_BATCH_SHIPMENTS_REQUEST),
    mergeMap((action) => {
      const { viewRecordsOrSelectedRecords, primaryRecordsOrLineItems, intl } = action;
      const state = getState();
      const { initiatedByUserId, requestUrl, requestBody } = ShipmentExportHelper.getBatchExportRequestParameters(
        state,
        viewRecordsOrSelectedRecords,
        primaryRecordsOrLineItems,
        intl,
        history,
      );
      return ExportApi.exportShipments(requestUrl, requestBody).pipe(
        mergeMap((jobId) => {
          showSuccessNotification();
          return of(Actions.exportShipmentsSuccess(jobId, initiatedByUserId));
        }),
        catchError(() => {
          showFailureNotification();
          return EMPTY;
        }),
      );
    }),
  );
};

export const initiateShipmentsCustomExportEpic = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(Actions.CUSTOM_EXPORT_SHIPMENTS_REQUEST, Actions.CUSTOM_EXPORT_BATCH_SHIPMENTS_REQUEST),
    mergeMap((action) => {
      const {
        viewRecordsOrSelectedRecords,
        extractId,
        selectedFulfillmentIds,
        searchTerm,
        fulfillmentPlanSetId,
        filters,
        isOnAdvancedSearch,
      } = action;
      const state = getState();

      const { requestBody, initiatedByUserId, requestUrl } = ShipmentExportHelper.getCustomExportRequestBody({
        state,
        viewRecordsOrSelectedRecords,
        extractId,
        selectedFulfillmentIds,
        searchTerm,
        fulfillmentPlanSetId,
        isOnAdvancedSearch,
        filters,
        history,
      });
      return ExportApi.customExportShipments(requestBody, requestUrl).pipe(
        mergeMap(({ jobGuids }) => {
          showSuccessNotification();
          const jobId = jobGuids && jobGuids[0];
          return of(Actions.exportShipmentsSuccess(jobId, initiatedByUserId));
        }),
        catchError(() => {
          showFailureNotification();
          return EMPTY;
        }),
      );
    }),
  );
};

export const downloadShipmentsExportEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.EXPORT_SHIPMENTS_COMPLETE),
    mergeMap((action) => {
      const state = getState();
      const downloadUrl = get(action, 'data.DownloadUrl') || get(action, 'data.downloadUrl');
      const jobId = get(action, 'messageGuid');
      const job = Global.selectExportShipmentsExportShipmentJobByJobId(state, {
        jobId,
      });
      const currentUserId = Global.selectUserCurrentUserId(state);
      const initiatedByUserId = get(job, 'initiatedByUserId');
      const errorMessage = get(action, 'data.errorMessage', '');
      if (errorMessage && errorMessage.length) {
        showFailureNotification();
        return of(Actions.exportShipmentsCleanup(jobId));
      }
      if (currentUserId === initiatedByUserId) {
        DocumentService.getExportDocumentActions(downloadUrl);
        NotificationHubService.showNotification(
          {
            notificationType: NotificationType.SHIPMENTS_EXPORT_COMPLETED,
            defaultTitle: messages.shipmentExportNotifDoneHeader.defaultMessage,
            defaultDescription: notificationDescriptionMessages.SHIPMENTS_EXPORT_COMPLETED.defaultMessage,
            metadataJson: JSON.stringify({ downloadUrl }),
          },
          {
            notificationType: NOTIFICATION_TYPE_INFO,
            title: <FormattedMessage {...messages.shipmentExportNotifDoneHeader} />,
            content: <FormattedMessage {...messages.shipmentExportNotifDoneBody} />,
            duration: NOTIFICATION_DURATION_IN_MS,
          },
        );
      }
      return of(Actions.exportShipmentsCleanup(jobId));
    }),
  );
};
